import { useEffect, type ReactElement } from 'react';

import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer';
import { cn } from '@/lib/utils';

import { useChatBranding, useChatConfig, useChatRoot, useChatStore } from './ChatProvider';
import { ChatHeader } from './components/ChatHeader';
import { ChatInput } from './components/ChatInput';
import { ChatLauncher } from './components/ChatLauncher';
import { ErrorOverlay } from './components/ErrorOverlay';
import { MessageList } from './components/MessageList';
import { WelcomeView } from './components/WelcomeView';
import { useIsMobile } from './hooks/useIsMobile';
import { isInitialWelcomeOnly } from './state/welcome';

interface ChatWindowProps {
  mode?: 'floating' | 'fullscreen';
  className?: string;
}

export function ChatWindow({ mode = 'floating', className }: ChatWindowProps): ReactElement {
  const branding = useChatBranding();
  const open = useChatStore((s) => s.ui.open);
  const setOpen = useChatStore((s) => s.setOpen);
  const isMobile = useIsMobile();
  const chatRoot = useChatRoot();

  useEffect(() => {
    if (!open || isMobile || mode === 'fullscreen') return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open, isMobile, mode, setOpen]);

  if (isMobile) {
    return (
      <>
        {!open && <ChatLauncher />}
        <Drawer open={open} onOpenChange={setOpen} container={chatRoot}>
          <DrawerContent
            className="flex h-dvh flex-col overflow-hidden p-0 data-[vaul-drawer-direction=bottom]:mt-0 data-[vaul-drawer-direction=bottom]:max-h-dvh data-[vaul-drawer-direction=bottom]:rounded-t-none data-[vaul-drawer-direction=bottom]:border-t-0"
            data-slot="chat-drawer"
          >
            <DrawerTitle className="sr-only">{branding.title}</DrawerTitle>
            <DrawerDescription className="sr-only">
              Janela de conversa com o assistente. Arraste para baixo para fechar.
            </DrawerDescription>
            <ChatBody />
          </DrawerContent>
        </Drawer>
      </>
    );
  }

  if (mode === 'fullscreen') {
    return (
      <div
        role="dialog"
        aria-label={branding.title}
        className={cn(
          'bg-background relative flex h-dvh w-full flex-col overflow-hidden',
          className,
        )}
      >
        <ChatBody hideMinimize />
      </div>
    );
  }

  return (
    <>
      {!open && <ChatLauncher />}
      {open && (
        <div
          role="dialog"
          aria-label={branding.title}
          className={cn(
            'bg-background border-border/60 shadow-primary/15 motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-bottom-4 motion-safe:duration-300',
            'fixed right-5 bottom-5 z-40 flex h-[640px] max-h-[calc(100dvh-2.5rem)] w-[400px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-3xl border shadow-2xl',
            className,
          )}
        >
          <ChatBody />
        </div>
      )}
    </>
  );
}

function ChatBody({ hideMinimize = false }: { hideMinimize?: boolean }): ReactElement {
  const config = useChatConfig();
  const isWelcome = useChatStore((s) => isInitialWelcomeOnly(s.messages));
  const showWelcomeView = isWelcome && config.enableWelcomeView;

  return (
    <>
      <ChatHeader showMinimize={!hideMinimize} hideLogo />
      {showWelcomeView ? (
        <WelcomeView />
      ) : (
        <div className="motion-safe:animate-in motion-safe:fade-in motion-safe:slide-in-from-top-2 motion-safe:duration-500 flex flex-1 flex-col overflow-hidden">
          <MessageList />
        </div>
      )}
      <div className="motion-safe:animate-in motion-safe:slide-in-from-top-4 motion-safe:fade-in motion-safe:duration-500">
        <ChatInput />
      </div>
      <ErrorOverlay />
    </>
  );
}
